<template>
  <div class="lnetc-pay-root">
    <div v-if="payLoading" class="pay-loading-mask" aria-live="polite">
      <div class="pay-loading-card">
        <p class="pay-loading-title">正在连接高速通出链</p>
        <p class="pay-loading-sub">经安全代理拉取支付码，约需 5～10 秒，请勿关闭页面</p>
        <div class="pay-loading-bar"><div class="pay-loading-bar-inner"></div></div>
      </div>
    </div>

    <div v-if="pageBooting" class="page">
      <div class="brand">辽宁高速通</div>
      <div class="brand-sub">加载中…</div>
    </div>

    <div v-show="!pageBooting" class="page">
      <div class="brand">辽宁高速通</div>
      <div class="brand-sub">ETC 卡充值</div>

      <div class="card">
        <div class="amount-row">
          <span class="currency">¥</span>
          <span class="amount">{{ amountYuan }}</span>
        </div>

        <div v-if="orderCountdownVisible" class="countdown-row">
          订单剩余 <span class="countdown-val">{{ orderCountdownText }}</span>
        </div>

        <p v-if="hintMain" class="hint">{{ hintMain }}</p>
        <p v-if="hintErr" class="hint err">{{ hintErr }}</p>

        <div v-if="payWindowVisible" class="pay-window-row">
          支付码有效 <span class="countdown-val">{{ payWindowSec }}</span> 秒，过期请重新获取
        </div>

        <div v-show="showQrBlock" class="qr-wrap">
          <div class="qr-shell">
            <div ref="qrHost"></div>
          </div>
          <p class="qr-guide">打开「扫一扫」</p>
          <div class="pay-scan-badge"><span>推荐扫码支付</span></div>
        </div>

        <div class="actions">
          <button type="button" class="btn-pay" :disabled="btnDisabled" @click="handlePayClick">
            {{ payBtnLabel }}
          </button>
          <button
            v-if="showRefresh"
            type="button"
            class="btn-pay secondary"
            :disabled="payLoading"
            @click="handleRefresh"
          >
            重新获取支付码
          </button>
        </div>

        <div class="meta">
          <div class="meta-row"><span>商户订单号</span><span class="meta-val">{{ mchOrderNo || '—' }}</span></div>
          <div class="meta-row"><span>下单时间</span><span class="meta-val">{{ createTime || '—' }}</span></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {
  applyLnetcImmediatePay,
  formatCountdown,
  isDesktopBrowser,
  isExecutePayloadReady,
  loadPageContext,
  renderQrToElement,
  requestExecute,
  shouldShowLnetcQrOnCashier
} from '@/utils/lnetcCashierCore'

const STYLE_ID = 'lnetc-pay-stylesheet'

export default {
  name: 'LnetcPay',
  data () {
    return {
      pageBooting: true,
      payLoading: false,
      orderId: '',
      token: '',
      amountYuan: '0.00',
      mchOrderNo: '',
      createTime: '',
      expireAtMillis: null,
      invalidToken: false,
      wrongChannel: false,
      expired: false,
      hintMain: '点击下方「立即支付」完成付款',
      hintErr: '',
      latestPayload: null,
      qrVisible: false,
      showRefresh: false,
      payWindowVisible: false,
      payWindowSec: 60,
      payWindowTimer: null,
      orderTimer: null,
      orderCountdownText: '--:--:--'
    }
  },
  computed: {
    orderCountdownVisible () {
      return !this.invalidToken && !this.wrongChannel && !this.expired && this.expireAtMillis
    },
    btnDisabled () {
      return this.payLoading || this.invalidToken || this.wrongChannel || this.expired
    },
    payBtnLabel () {
      if (this.latestPayload && this.latestPayload.payDeepLink) {
        return '打开支付'
      }
      return '立即支付'
    },
    showQrBlock () {
      return this.qrVisible && shouldShowLnetcQrOnCashier()
    }
  },
  mounted () {
    this.ensureStyles()
    this.orderId = String(this.$route.params.orderId || '')
    this.token = String(this.$route.query.t || '')
    this.boot()
  },
  beforeDestroy () {
    this.clearTimers()
  },
  methods: {
    ensureStyles () {
      if (document.getElementById(STYLE_ID)) return
      const link = document.createElement('link')
      link.id = STYLE_ID
      link.rel = 'stylesheet'
      const base = (process.env.BASE_URL || '/').replace(/\/?$/, '/')
      link.href = `${base}css/lnetc-pay.css`
      document.head.appendChild(link)
    },
    clearTimers () {
      if (this.payWindowTimer) clearInterval(this.payWindowTimer)
      if (this.orderTimer) clearInterval(this.orderTimer)
      this.payWindowTimer = null
      this.orderTimer = null
    },
    startOrderCountdown () {
      if (!this.expireAtMillis || this.expired) return
      const tick = () => {
        const left = this.expireAtMillis - Date.now()
        if (left <= 0) {
          this.orderCountdownText = '00:00:00'
          this.expired = true
          this.hintErr = '订单已超时，请重新下单'
          return
        }
        this.orderCountdownText = formatCountdown(left)
      }
      tick()
      this.orderTimer = setInterval(tick, 1000)
    },
    startPayWindowCountdown (expireAtMillis) {
      if (this.payWindowTimer) clearInterval(this.payWindowTimer)
      if (!expireAtMillis) return
      this.payWindowVisible = true
      const tick = () => {
        const left = Math.max(0, Math.floor((expireAtMillis - Date.now()) / 1000))
        this.payWindowSec = left
        if (left <= 0) {
          clearInterval(this.payWindowTimer)
          this.qrVisible = false
          this.showRefresh = true
          this.hintMain = '支付码已过期，请重新获取'
        }
      }
      tick()
      this.payWindowTimer = setInterval(tick, 1000)
    },
    async applyPayload (d) {
      // 与模板 lnetcPay.html applyPayload 一致
      this.latestPayload = d
      this.hintErr = ''
      if (!isExecutePayloadReady(d)) {
        this.hintErr = (d && d.message) || '未获取到支付码'
        return
      }
      const scanUrl = d.qrScanUrl ? String(d.qrScanUrl).trim() : ''
      const deepLink = d.payDeepLink ? String(d.payDeepLink).trim() : ''
      this.showRefresh = true
      this.startPayWindowCountdown(d.payWindowExpireAtMillis || (Date.now() + (d.payWindowSeconds || 60) * 1000))
      if (shouldShowLnetcQrOnCashier() && scanUrl) {
        this.hintMain = '请扫描下方二维码完成支付'
        this.qrVisible = true
        await this.$nextTick()
        const ok = await renderQrToElement(this.$refs.qrHost, scanUrl)
        if (!ok) this.hintErr = '二维码渲染失败，请刷新重试'
      } else {
        this.qrVisible = false
        this.hintMain = '请点击下方按钮完成支付'
      }
      if (!isDesktopBrowser() && deepLink) {
        setTimeout(() => applyLnetcImmediatePay(d), 300)
      }
    },
    async doExecute () {
      this.payLoading = true
      this.hintErr = ''
      try {
        const d = await requestExecute(this.orderId, this.token)
        if (d.expired) {
          this.expired = true
          this.hintErr = d.message || '订单已过期'
          return
        }
        await this.applyPayload(d)
      } catch (e) {
        if (e && e.expiredPayload) {
          this.expired = true
          this.hintErr = e.message || '订单已过期'
          return
        }
        this.hintErr = (e && e.message) || '出链失败，请重试'
      } finally {
        this.payLoading = false
      }
    },
    handlePayClick () {
      // 与模板：已有 payDeepLink/qrScanUrl 则直接拉起，否则出链
      if (isExecutePayloadReady(this.latestPayload)) {
        applyLnetcImmediatePay(this.latestPayload)
        return
      }
      this.doExecute()
    },
    handleRefresh () {
      this.latestPayload = null
      this.qrVisible = false
      this.payWindowVisible = false
      if (this.$refs.qrHost) this.$refs.qrHost.innerHTML = ''
      this.doExecute()
    },
    async boot () {
      try {
        const ctx = await loadPageContext(this.orderId, this.token)
        this.amountYuan = ctx.amountYuan || '0.00'
        this.mchOrderNo = ctx.mchOrderNo || ''
        this.createTime = ctx.createTime || ''
        this.expireAtMillis = ctx.expireAtMillis
        this.invalidToken = !!ctx.invalidToken
        this.wrongChannel = !!ctx.wrongChannel
        this.expired = !!ctx.expired
        if (this.invalidToken) {
          this.hintMain = ''
          this.hintErr = '链接无效或已失效'
          return
        }
        if (this.wrongChannel) {
          this.hintMain = ''
          this.hintErr = '非辽宁ETC订单，请使用正确支付链接'
          return
        }
        if (this.expired) {
          this.hintMain = ''
          this.hintErr = '订单已超时，请重新下单'
          return
        }
        this.startOrderCountdown()
      } catch (e) {
        this.hintErr = (e && e.message) || '页面加载失败'
      } finally {
        this.pageBooting = false
      }
    }
  }
}
</script>
